/**
 * SettingsModal Component
 * Admin controls for dashboard configuration
 */

import React, { useState, useEffect } from 'react';
import { X, Download, Trash2 } from 'lucide-react';
import { useDashboardStore } from '../store/dashboardStore';
import toast from 'react-hot-toast';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const metrics = useDashboardStore((state) => state.metrics);
  const refreshIntervalSeconds = useDashboardStore((state) => state.refreshIntervalSeconds);
  const setRefreshIntervalSeconds = useDashboardStore((state) => state.setRefreshIntervalSeconds);
  const [refreshInterval, setRefreshInterval] = useState(refreshIntervalSeconds);
  const events = useDashboardStore((state) => state.events);
  const setEvents = useDashboardStore((state) => state.setEvents);

  useEffect(() => {
    if (isOpen) {
      setRefreshInterval(refreshIntervalSeconds);
    }
  }, [isOpen, refreshIntervalSeconds]);

  const handleSaveSettings = () => {
    setRefreshIntervalSeconds(refreshInterval);
    localStorage.setItem(
      'dashboardSettings',
      JSON.stringify({ refreshIntervalSeconds: refreshInterval })
    );
    toast.success('Settings saved!');
    onClose();
  };

  const handleExportMetrics = () => {
    if (!metrics) {
      toast.error('No metrics to export');
      return;
    }

    const csv = [
      ['Metric', 'Value'],
      ['Total Requests', metrics.totalRequests || 0],
      ['Cache Hits', metrics.cacheHits || 0],
      ['Cache Misses', metrics.cacheMisses || 0],
      ['Hit Rate (%)', ((metrics.hitRate || 0) * 100).toFixed(2)],
      ['Avg Latency (ms)', metrics.averageLatency || 0],
      ['Tokens Saved', metrics.tokensSaved || 0],
      ['Cost Saved ($)', (metrics.costSaved || 0).toFixed(2)],
      ['Active Agents', metrics.activeAgents || 0],
      ['Total Agents', metrics.totalAgents || 0],
      ['Uptime (ms)', metrics.uptime || 0],
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lemma-metrics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('Metrics exported!');
  };

  const handleClearEventLog = () => {
    setEvents([]);
    toast.success('Event log cleared');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">⚙️ Settings</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Dashboard Refresh Interval */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              📊 Dashboard Refresh Interval
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="2"
                max="30"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-semibold text-gray-600 w-16 text-right">
                {refreshInterval}s
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              How often to fetch updated metrics from the server
            </p>
          </div>

          <hr className="border-gray-200" />

          {/* Export Metrics */}
          <button
            onClick={handleExportMetrics}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg font-medium transition"
          >
            <Download size={16} />
            📥 Export Metrics as CSV
          </button>

          {/* Clear Event Log */}
          <button
            onClick={handleClearEventLog}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium transition"
          >
            <Trash2 size={16} />
            🗑️ Clear Event Log ({events.length} events)
          </button>

          <hr className="border-gray-200" />

          {/* Server Status */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2">
              API Status
            </h3>
            <div className="space-y-1 text-xs text-gray-600">
              <div>API: <span className="text-green-600 font-semibold">http://localhost:3001</span></div>
              <div>WS: <span className="text-green-600 font-semibold">ws://localhost:3001</span></div>
              <div>Dashboard: <span className="text-green-600 font-semibold">localhost:5173</span></div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded font-medium transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveSettings}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};
